import { BrowserRouter as Router } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginApp from './LoginApp';
import MainApp from './MainApp';
import IntermediateFeature from './components/IntermediateFeature';
import LoadingScreen from './components/LoadingScreen';

// Component that decides which app to render based on authentication
const AppRouter = () => {
  const { isAuthenticated, isFullyAuthenticated } = useAuth();
  const [showLoading, setShowLoading] = useState(false);
  const previousAuthStateRef = useRef({
    isAuthenticated: false,
    isFullyAuthenticated: false
  });

  // Check if intermediate feature is completed
  const hasCompletedIntermediateFeature = sessionStorage.getItem('intermediateFeatureCompleted') === 'true';

  // Check if user has entered credentials but not completed 2FA
  const hasCredentials = sessionStorage.getItem('username') && sessionStorage.getItem('tempPassword');

  // Check if user has a token
  const hasToken = sessionStorage.getItem('token');

  // Check if user is a super admin
  const isSuperAdmin = sessionStorage.getItem('isSuperAdmin') === 'true';

  // Track authentication state changes for all users
  useEffect(() => {
    const currentAuthState = {
      isAuthenticated,
      isFullyAuthenticated
    };

    const previousAuthState = previousAuthStateRef.current;

    // Show loading screen when transitioning from not authenticated to fully authenticated
    // For all users (both regular and super admins)
    if (!previousAuthState.isFullyAuthenticated &&
      currentAuthState.isFullyAuthenticated &&
      hasToken) {
      setShowLoading(true);

      // Hide loading screen after 2 seconds
      const timer = setTimeout(() => {
        setShowLoading(false);
      }, 2000);

      return () => clearTimeout(timer);
    }

    // Update the ref with current state
    previousAuthStateRef.current = currentAuthState;
  }, [isAuthenticated, isFullyAuthenticated, hasToken]);

  // Show loading screen if transitioning (for all users)
  if (showLoading) {
    return <LoadingScreen />;
  }

  // If fully authenticated and intermediate feature completed, show main app
  if (isFullyAuthenticated && hasCompletedIntermediateFeature && hasToken) {
    return <MainApp />;
  }

  // If fully authenticated but intermediate feature not completed, show intermediate feature
  if (isFullyAuthenticated && !hasCompletedIntermediateFeature) {
    return <IntermediateFeature />;
  }

  // If user has credentials but not fully authenticated (2FA pending), show login app
  if (hasCredentials && !isFullyAuthenticated) {
    return <LoginApp />;
  }

  // If authenticated but not fully authenticated (2FA pending), show login app
  if (isAuthenticated && !isFullyAuthenticated) {
    return <LoginApp />;
  }

  // If not authenticated at all and no credentials, show login app
  if (!isAuthenticated && !hasCredentials && !hasToken) {
    return <LoginApp />;
  }

  // Default fallback - show login app
  return <LoginApp />;
};

const App = () => {
  return (
    <Router>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </Router>
  );
};

export default App;